<template>
  <div :class="['login', { 'login--contrast': !isRegisterByInvitation }]">
    <div class="container">
      <div class="row row-lg-right row-xl-right">
        <div class="col col-xs-12 col-md-6 col-lg-4 col-lg-6">
          <div class="login__card">
            <el-card class="box-card" :body-style="{ padding: '40px 36px' }">
              <h1 class="login__title">
                Sign up to AllTeams
              </h1>
              <div
                :class="[
                  'login__no-account',
                  {
                    'login__no-account--padding-bottom': isRegisterByInvitation,
                  },
                ]"
              >
                Already a member?
                <router-link :to="{ name: 'Login' }">
                  <el-link>Sign in</el-link>
                </router-link>
              </div>

              <external-login />

              <el-divider
                content-position="center"
                v-if="!isRegisterByInvitation"
                >or</el-divider
              >

              <div class="login__form">
                <el-form
                  @submit.native.prevent="validateForm"
                  label-width="0"
                  :rules="registerFormRules"
                  :model="registerForm"
                  ref="registerForm"
                >
                  <el-form-item :error="errors.email" prop="email">
                    <el-input
                      v-model="registerForm.email"
                      placeholder="Email"
                      :disabled="Boolean(invitationEmail)"
                    />
                  </el-form-item>

                  <el-form-item :error="errors.password" prop="password">
                    <el-input
                      placeholder="Password"
                      v-model="registerForm.password"
                      show-password
                    ></el-input>
                  </el-form-item>

                  <el-form-item
                    :error="errors.confirmPassword"
                    prop="confirmPassword"
                  >
                    <el-input
                      placeholder="Re-enter password"
                      v-model="registerForm.confirmPassword"
                      show-password
                    ></el-input>
                  </el-form-item>

                  <el-button
                    type="primary"
                    native-type="submit"
                    class="login__submit-button"
                    :disabled="isButtonDisabled('createAccount')"
                  >
                    Create account
                  </el-button>
                </el-form>
              </div>
            </el-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/common/Icon.vue';
import ExternalLogin from '@/components/unregisteredUsersPages/ExternalLogin.vue';

import { AUTH_REQUEST } from '@/store/mutationTypes.js';
import { register, acceptInvitation } from '@/api/user.js';
import { disableButton } from '@/mixins/disableButton.js';
import { showMessage } from '@/utils/messages.js';
import { parseErrorsByFields } from '@/utils/index.js';

export default {
  name: 'Registration',
  components: {
    Icon,
    ExternalLogin,
  },
  mixins: [disableButton],
  data() {
    return {
      registerForm: {
        email: '',
        password: '',
        confirmPassword: '',
      },
      errors: {
        email: '',
        password: '',
        confirmPassword: '',
      },
      registerFormRules: {
        email: [
          {
            required: true,
            trigger: 'input',
            message: 'Please input your email',
          },
        ],
        password: [
          {
            required: true,
            trigger: 'input',
            message: 'Please input password',
          },
        ],
        confirmPassword: [
          {
            required: true,
            trigger: 'input',
            message: 'Please confirm password',
          },
        ],
      },
    };
  },
  computed: {
    isFormFilled() {
      const { registerForm } = this;
      return Object.values(registerForm).every((value) => !!value);
    },
    invitationCode() {
      return this.$route.query.code || null;
    },
    invitationEmail() {
      return this.$route.query.email || null;
    },
    isRegisterByInvitation() {
      const { invitationCode } = this;
      return Boolean(invitationCode);
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    async requestCreateAccount() {
      this.disableButton('createAccount');
      const { email, password, confirmPassword } = this.registerForm;

      const { success, errors } = await register({
        email,
        password,
        confirmPassword,
      });
      this.enableButton('createAccount');

      if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      } else if (success) {
        this.showMessage({
          type: 'success',
          message:
            'Confirmation has been sent to your email. Please check your email.',
        });
        this.clearForm();
        this.clearErrors();
        this.$router.push({
          path: '/',
        });
      }
    },
    async requestAcceptInvitation() {
      this.disableButton('createAccount');
      const { invitationCode } = this;
      const { email, password, confirmPassword } = this.registerForm;

      const { success, errors } = await acceptInvitation({
        email,
        password,
        confirmPassword,
        code: invitationCode,
      });

      this.enableButton('createAccount');

      if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      } else if (success) {
        this.showMessage({
          type: 'success',
          message: 'Please login with your credentials.',
        });
        this.clearForm();
        this.clearErrors();
        this.$router.push({
          path: '/',
        });
      }
    },
    validateForm() {
      const { isRegisterByInvitation, registerForm, isFormFilled } = this;

      if (this.$refs.registerForm) {
        this.$refs.registerForm.validate();
      }

      if (isFormFilled) {
        if (isRegisterByInvitation) {
          this.requestAcceptInvitation();
        } else {
          this.requestCreateAccount();
        }
      }
    },
    clearForm() {
      const { registerForm } = this;
      for (const fieldName in registerForm) {
        registerForm[fieldName] = '';
      }
    },
    clearErrors() {
      for (const fieldName in this.errors) {
        this.errors[fieldName] = '';
      }
    },
  },
  created() {
    const { isRegisterByInvitation, invitationEmail } = this;

    if (isRegisterByInvitation) {
      this.registerForm.email = invitationEmail;
    }
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';
</style>
